import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Table, { ColumnDefinitionType } from "../../../../../components/Table";
import Confirm from "../../../../../components/Confirm";
import FormSelectBox from "../../../../../components/Form/FormSelectBox";
import { cargarCombo } from "../../../../../services/Service";
import { EmpresaGestora } from "../../../../../types/EmpresaGestora";
import { EmpresaGestoraSupervisora } from "../../../../../types/EmpresaSupervisora";
import { toast } from "react-toastify";

type EmpresasGestorasProps = {
    empresasGestoras: EmpresaGestoraSupervisora[],
    setEmpresasGestoras: (value: EmpresaGestoraSupervisora[]) => void,
    idEmpresaSupervisora?: number,
    edit: boolean,
    view: boolean,
}

const SeccionEmpresasGestoras = ({ edit, view, empresasGestoras, setEmpresasGestoras, idEmpresaSupervisora }: EmpresasGestorasProps) => {
    const [empresasGestorasCatalogo, setEmpresasGestorasCatalogo] = useState<EmpresaGestora[]>([]);
    const [empresasGestorasDisponibles, setEmpresasGestorasDisponibles] = useState<EmpresaGestora[]>([]);
    const [idEmpresaGestoraSeleccionada, setIdEmpresaGestoraSeleccionada] = useState<any>('');
    const [showModal, setShowModal] = useState<boolean>(false);
    const toggle = () => setShowModal(!showModal);
    const [data, setData] = useState<EmpresaGestoraSupervisora | null>(null);

    useEffect(() => {
        // Una Empresa Gestora solo puede pertenecer a una Empresa Supervisora: en edición se excluyen las de otras supervisoras
        const path = idEmpresaSupervisora
            ? `empresasSupervisoras/empresasGestorasActivas?idEmpresaSupervisora=${idEmpresaSupervisora}`
            : 'empresasSupervisoras/empresasGestorasActivas';
        cargarCombo(path, setEmpresasGestorasCatalogo);
    }, [idEmpresaSupervisora])

    useEffect(() => {
        const disponibles = (empresasGestorasCatalogo || []).filter(
            (eg: EmpresaGestora) => !empresasGestoras.some(asociada => asociada.idEmpresaGestora === eg.id)
        );
        setEmpresasGestorasDisponibles(disponibles);
    }, [empresasGestorasCatalogo, empresasGestoras])

    const eliminar = () => {
        if (data) {
            const restantes = empresasGestoras.filter(eg => eg.idEmpresaGestora !== data.idEmpresaGestora);
            setEmpresasGestoras(restantes);
        }
        toggle();
    }

    const agregar = () => {
        if (!idEmpresaGestoraSeleccionada) {
            toast.error('Debe seleccionar al menos una empresa gestora');
            return;
        }
        const yaAsociada = empresasGestoras.some(eg => eg.idEmpresaGestora === Number(idEmpresaGestoraSeleccionada));
        if (yaAsociada) {
            toast.error('La empresa seleccionada ya se encuentra asociada');
            return;
        }
        const empresaGestora = empresasGestorasCatalogo.find((eg: EmpresaGestora) => eg.id === Number(idEmpresaGestoraSeleccionada));
        if (empresaGestora) {
            const nuevaRelacion: EmpresaGestoraSupervisora = {
                idEmpresaGestora: empresaGestora.id,
                idEmpresaSupervisora: idEmpresaSupervisora || undefined,
                activo: true,
                tipoIdentificacion: empresaGestora.tipoDocumento,
                nroIdentificacion: empresaGestora.nroIdentificacion,
                razonSocial: empresaGestora.nombreEmpresa,
                activoGestora: empresaGestora.activo === true,
            }
            setEmpresasGestoras([...empresasGestoras, nuevaRelacion]);
            setIdEmpresaGestoraSeleccionada('');
        }
    }

    const columns: ColumnDefinitionType<EmpresaGestoraSupervisora, keyof EmpresaGestoraSupervisora>[] = []
    columns.push({
        key: 'tipoIdentificacion',
        header: 'Tipo Identificación',
    });
    columns.push({
        key: 'nroIdentificacion',
        header: 'Número Identificación',
    });
    columns.push({
        key: 'razonSocial',
        header: 'Razón Social',
    });
    columns.push({
        key: 'activoGestora',
        header: 'Estado',
        cell: (data: EmpresaGestoraSupervisora) => <>{data.activoGestora ? 'ACTIVA' : 'INACTIVA'}</>
    });
    if (!view) {
        columns.push({
            key: 'idEmpresaGestora',
            header: 'Acciones',
            cell: (data: EmpresaGestoraSupervisora) =>
                <>
                    <Link to="#" title="Eliminar"
                        onClick={(e) => {
                            e.stopPropagation();
                            setData(data);
                            setShowModal(true);
                        }}
                    >
                        <i className="fa fa-trash-o mr-2 text-danger" aria-hidden="true"></i>
                    </Link>
                </>
        });
    }

    return (
        <>
            <Confirm isOpen={showModal} toggle={toggle} title='Eliminar' yesFn={eliminar}>
                <div className="form-horizontal">
                    <div className="row">
                        {<p>Está seguro que desea eliminar la asociación con la Empresa Gestora {data?.razonSocial}?</p>}
                    </div>
                </div>
            </Confirm>
            <div className="card mt-4">
                <div className="card-body">
                    <div className="row">
                        {!view &&
                            <div className="row mt-2 mb-3 justify-content-center">
                                <FormSelectBox
                                    title={'Empresa Gestora'}
                                    label={'Empresa Gestora'}
                                    id={'idEmpresaGestora'}
                                    name={'idEmpresaGestora'}
                                    onChange={(e) => setIdEmpresaGestoraSeleccionada(e.target.value)}
                                    optionId={'id'}
                                    optionLabel={'nombreEmpresa'}
                                    value={idEmpresaGestoraSeleccionada}
                                    options={empresasGestorasDisponibles}
                                    isDisabled={edit && view}
                                />
                            </div>
                        }

                        <h4 className="m-t-0 header-title">
                            Empresas Gestoras
                            {!view &&
                                <Link to="#" title="Agregar" className="btn-custom btn-rounded waves-effect waves-light pull-right btn-sm" style={{ marginTop: '-8px' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        agregar();
                                    }}>
                                    <i className="fa fa-plus margin-right-10"></i> Agregar
                                </Link>
                            }
                        </h4>

                        <div className="table-responsive">
                            <br />
                            <Table data={empresasGestoras || []} columns={columns}></Table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default SeccionEmpresasGestoras;
